import { Component, inject, effect, Renderer2, signal } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SidebarService } from '../../services/sidebar.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../../core/services/auth.service';
import { filter } from 'rxjs';

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
})
export class Sidebar {
  sidebarService = inject(SidebarService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly routeContext = signal(this.parseRouteContext(this.router.url));

  // Expose member signal to template
  member = this.authService.member;

  constructor() {
    effect(() => {
      if (this.sidebarService.isOpen()) {
        this.renderer.addClass(this.document.body, 'sidebar-open');
      } else {
        this.renderer.removeClass(this.document.body, 'sidebar-open');
      }
    });

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.routeContext.set(this.parseRouteContext(event.urlAfterRedirects));
      });
  }

  private parseRouteContext(rawUrl: string) {
    const url = this.router.parseUrl(rawUrl);
    const path = url.root.children['primary']?.segments.map((s) => s.path).join('/') ?? '';
    const from = String(url.queryParams['from'] ?? '');
    const editId = String(url.queryParams['editId'] ?? '');
    const forcedSection = url.queryParams['searchOrigin'] === '1'
      ? String(url.queryParams['originSection'] ?? '')
      : '';

    return { path, from, editId, forcedSection };
  }

  navigateToTransactions(): void {
    this.routeContext.set({
      path: 'transactions',
      from: '',
      editId: '',
      forcedSection: '',
    });
    this.sidebarService.close();
    this.router.navigate(['/transactions']);
  }

  navigateToAddTransactions(): void {
    this.routeContext.set({
      path: 'add-transactions',
      from: '',
      editId: '',
      forcedSection: '',
    });
    this.sidebarService.close();
    this.router.navigate(['/add-transactions']);
  }

  isStandaloneSectionActive(section: string): boolean {
    const { path, forcedSection } = this.routeContext();
    if (forcedSection) return forcedSection === section;
    return path === section;
  }

  isDashboardActive(): boolean {
    const { path, from, forcedSection } = this.routeContext();
    if (forcedSection) return forcedSection === 'dashboard';

    const isTransactionDetail = path.startsWith('transactions/') && path.split('/').length === 2;
    return path === 'dashboard' || (isTransactionDetail && from === 'dashboard');
  }

  isClientsActive(): boolean {
    const { path, forcedSection } = this.routeContext();
    if (forcedSection) return forcedSection === 'clients';

    return path === 'clients' || path.startsWith('clients/');
  }

  isTransactionsActive(): boolean {
    const { path, from, editId, forcedSection } = this.routeContext();
    if (forcedSection) return forcedSection === 'transactions';

    const isTransactionsRoute = path === 'transactions' || path.startsWith('transactions/');
    const isFromDashboardDetail = path.startsWith('transactions/') && from === 'dashboard';
    const isTransactionsEditRoute = path === 'add-transactions' && from === 'transactions' && !!editId;
    
    return (isTransactionsRoute && !isFromDashboardDetail) || isTransactionsEditRoute;
  }

  isAddTransactionsActive(): boolean {
    const { path, from, editId, forcedSection } = this.routeContext();
    if (forcedSection) return forcedSection === 'add-transactions';

    return path === 'add-transactions' && (from !== 'transactions' || !editId);
  }

  isRfiActive(): boolean {
    const { path, from, forcedSection } = this.routeContext();
    if (forcedSection) return forcedSection === 'rfi';

    return path === 'rfi' || (path.startsWith('investigations/') && from === 'rfi');
  }

  isInvestigationsActive(): boolean {
    const { path, from, forcedSection } = this.routeContext();
    if (forcedSection) return forcedSection === 'investigations';

    return (path === 'investigations' || path.startsWith('investigations/')) && from !== 'rfi';
  }

  isNotificationsActive(): boolean {
    const { path, forcedSection } = this.routeContext();
    if (forcedSection) return forcedSection === 'notifications';

    return path === 'notifications';
  }
}
